import api from '../api/axios';
import ReactGA from 'react-ga4';

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;
const APP_ENV = import.meta.env.VITE_APP_ENV || 'development';

const EXCLUDED_EMAILS = [
  'joshiayush000@gmail.com',
  'JD0882@outlook.com',
  'ayushinix72@gmail.com',
  'ayush@antaryatra-app.com' // Added professional email
];

const EXCLUDED_HOSTNAMES = [
  'localhost',
  '127.0.0.1',
  'antaryatra-dev.vercel.app'
];

/**
 * 🧘 Sanctuary Tracker (AYI Client)
 * 
 * A privacy-first, non-extractive tracking utility for Antaryatra.
 * Designed to measure healing impact without surveillance.
 * 
 * IMPROVED: Now handles GA4 environments and developer suppression more robustly.
 */

class SanctuaryTracker {
  constructor() {
    this.queue = [];
    this.isProcessing = false;
    this.sessionId = this._getOrCreateSessionId();
    this.isGAInitialized = false;

    // 🛑 STRICT ENFORCEMENT: Only initialize in Production
    if (GA_MEASUREMENT_ID && GA_MEASUREMENT_ID !== 'G-XXXXXXX' && APP_ENV === 'production') {
      ReactGA.initialize(GA_MEASUREMENT_ID);
      this.isGAInitialized = true;
      console.log("🧘 [AYI] Sanctuary Production Analytics Active");
    } else {
      console.log(`🙈 [AYI] Analytics Silenced (${APP_ENV})`);
    }
  }

  /**
   * Internal: Checks if current user should be excluded from tracking.
   */
  _isInternalUser() {
    // 1. Bot Detection
    const isBot = /bot|googlebot|crawler|spider|robot|crawling/i.test(navigator.userAgent) || navigator.webdriver;
    if (isBot) return true;

    // 2. Hostname Check (Exclude local dev environments)
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return true;
    }

    // 3. Developer Flag Check
    if (localStorage.getItem('ayi_internal_user') === 'true') {
      return true;
    }

    // 4. Logged-in User Email Check
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return false;
      const user = JSON.parse(userStr);
      const isInternal = user?.email && EXCLUDED_EMAILS.includes(user.email.toLowerCase());
      
      // If we find an internal email, set the flag persistently for this browser
      if (isInternal) {
        localStorage.setItem('ayi_internal_user', 'true');
      }
      
      return isInternal;
    } catch (e) {
      return false;
    }
  }

  /**
   * Internal: Generates a temporary session ID that resets on browser close.
   */
  _getOrCreateSessionId() {
    let sid = sessionStorage.getItem('ayi_sid');
    if (!sid) {
      sid = Math.random().toString(36).substring(2, 15);
      sessionStorage.setItem('ayi_sid', sid);
    }
    return sid;
  }

  /**
   * Tracks a community insight event.
   * @param {string} eventName - Name of the event (e.g. 'BREATHE_COMPLETE')
   * @param {object} metadata - Additional context (e.g. { duration: 180 })
   */
  track(eventName, metadata = {}) {
    // 🛑 SILENCE ON STAGING: Only track events in production
    if (APP_ENV !== 'production') return;

    const isInternal = this._isInternalUser();
    
    // Suppress GA4 for internal users in production to keep data clean
    if (isInternal) {
      console.log(`[AYI/GA] Production traffic suppressed for internal user: ${eventName}`);
      return;
    }

    const event = {
      eventName,
      metadata: {
        ...metadata,
        sid: this.sessionId,
        url: window.location.pathname,
        env: APP_ENV,
        traffic_type: isInternal ? 'internal' : 'user'
      },
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    };

    // 🌐 Send to active GA4 Property
    if (this.isGAInitialized) {
      // Modern GA4 structure: event_name, parameters
      ReactGA.event(eventName.toLowerCase(), {
        ...event.metadata,
        // Map common fields to GA4 recommended names where possible
        value: metadata.score || metadata.duration || undefined,
        item_id: metadata.articleId || metadata.providerId || undefined,
        item_name: metadata.title || metadata.name || undefined,
        method: metadata.method || undefined,
        // Set debug_mode if internal to allow filtering in GA4 UI
        debug_mode: isInternal
      });
    }

    // ☁️ Internal AYI Analytics
    console.log(`[AYI] Insight captured: ${eventName}`, event.metadata);
    this.queue.push(event);
    this._processQueue();
  }

  trackPageView(path) {
    // 🛑 SILENCE ON STAGING: Only track pageviews in production
    if (APP_ENV !== 'production') return;

    const isInternal = this._isInternalUser();
    
    if (isInternal) {
      console.log(`[AYI/GA] Pageview suppressed for internal user: ${path}`);
      return;
    }

    if (this.isGAInitialized) {
      ReactGA.send({ 
        hitType: "pageview", 
        page: path,
        title: document.title,
        app_env: APP_ENV,
        traffic_type: isInternal ? 'internal' : 'user'
      });
    }
  }

  async _processQueue() {
    if (this.isProcessing || this.queue.length === 0) return;

    this.isProcessing = true;
    const event = this.queue.shift();

    try {
      await api.post('/track', event);
    } catch (error) {
      // Silent fail for analytics
    } finally {
      this.isProcessing = false;
      this._processQueue();
    }
  }

  // Common Event Helpers
  trackOnboarding(step, label) {
    this.track('onboarding_step', { step, label });
  }

  trackWisdom(articleId, title) {
    this.track('wisdom_read', { articleId, title });
  }

  trackImpact(action, category) {
    this.track('sanctuary_impact', { action, category });
  }
}

const tracker = new SanctuaryTracker();
export default tracker;

